/**
 * Data transformation utilities for engagement analytics
 * 
 * These functions transform API responses from the engagement analytics endpoints
 * into formats compatible with the dashboard charts and components.
 */

import moment from 'moment-timezone';
import HistogramModel from '../models/analytics/HistogramModel';
import DatedUsageModel from '../models/analytics/api/DatedUsageModel';
import SessionReview from '../models/analytics/SessionReview';
import {
    TopLevelMetrics,
    OrganizationEngagement,
    TransferReasonsResponse,
    TransferReasonData as ApiTransferReasonData,
    AppBreakdown as ApiAppBreakdown,
    ContainedCallsData,
    LegacyTransferReason,
    TransferReasonsApiResponse,
    GroupedHistogramData,
    HistogramEntry
} from '../api/controllers/engagementAnalyticsReporting';
import {
    TransferCategoryData,
    TransferReason,
    TransferReasonData as ChartTransferReasonData,
    TRANSFER_REASON_IS_PLANNED
} from '../components/charts/TransfersChart';
import { DATE_FORMAT } from '../models/analytics/AnalyticsDateFormat';
import { filterInternalVoicifyTags } from './tagVisibility';

const ANALYTICS_DATE_ONLY_FORMAT = 'YYYY-MM-DD';
const ANALYTICS_API_DATE_FORMAT = 'YYYY-MM-DDTHH:mm:ss.SSSZ';
const ANALYTICS_API_DATE_FORMAT_NO_OFFSET = 'YYYY-MM-DDTHH:mm:ss.SSS';
const ANALYTICS_INPUT_DATE_FORMATS_NO_OFFSET = [
    ANALYTICS_DATE_ONLY_FORMAT,
    DATE_FORMAT,
    'YYYY-MM-DD HH:mm',
    ANALYTICS_API_DATE_FORMAT_NO_OFFSET,
    'YYYY-MM-DDTHH:mm:ss'
];
const ANALYTICS_INPUT_DATE_FORMATS = [
    ANALYTICS_DATE_ONLY_FORMAT,
    DATE_FORMAT,
    'YYYY-MM-DD HH:mm',
    ANALYTICS_API_DATE_FORMAT_NO_OFFSET,
    'YYYY-MM-DDTHH:mm:ss',
    'YYYY-MM-DDTHH:mm:ssZ',
    ANALYTICS_API_DATE_FORMAT
];

// ==================== TYPES ====================

/**
 * App breakdown format used by dashboard charts
 */
export interface DashboardAppBreakdown {
    appName: string;
    count: number;
    english?: number;
    spanish?: number;
}

/**
 * Histogram data point format for charts
 */
export interface HistogramDataPoint {
    x: string;         // Date label (e.g., "Jan 15")
    y: number;         // Value
    color: string;     // Color for the point
    fullDate: string;  // Full ISO date string
}

/**
 * Histogram dataset format for multi-line charts
 */
export interface HistogramDataset {
    id: string;        // Dataset identifier (e.g., "All Conversations")
    color: string;     // Line color
    data: HistogramDataPoint[];
}

/**
 * Healthcare metrics format for dashboard tiles
 */
export interface DashboardHealthcareMetrics {
    laborHoursSaved: number;
    offHoursCalls: number;
    offHoursScheduledAppts: number;
    containedCallsCount: number;
    containableCallsCount: number;
    totalEngagements: number;
    newPatientBookings: number;
    laborHoursSavedApps: DashboardAppBreakdown[];
    offHoursApps: DashboardAppBreakdown[];
    offHoursScheduledApptsApps: DashboardAppBreakdown[];
    newPatientBookingsApps: DashboardAppBreakdown[];
}

/**
 * Food ordering metrics format for dashboard tiles
 */
export interface DashboardFoodOrderingMetrics {
    upsellSuccessRate: number;
    successfulUpsells: number;
    upsellAttempts: number;
    averageTicketSize: number;
    totalSold: number;
    ordersPlaced: number;
    questionsAnswered: number;
    laborHoursSaved: number;
    upsoldRevenueGenerated: number;
    upsellApps: DashboardAppBreakdown[];
    ticketApps: DashboardAppBreakdown[];
    questionsApps: DashboardAppBreakdown[];
    laborHoursSavedApps: DashboardAppBreakdown[];
}

/**
 * Contained call data format for appointments managed chart
 */
export interface DashboardContainedCallData {
    type: string;
    count: number;
    color: string;
    apps: DashboardAppBreakdown[];
    notContainedCount?: number;
    notContainedApps?: DashboardAppBreakdown[];
}

/**
 * Call record for the calls table/export
 */
export interface DashboardCallRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    transcriptLink: string;
    category: string;
    tags: string[];
    location: string;
}

/**
 * Transfer record for the transfers table/export
 */
export interface DashboardTransferRecord {
    id: string;
    date: string;
    time: string;
    phoneLastFour: string;
    transcriptLink: string;
    transferType: 'Planned' | 'Unplanned';
    transferReason: TransferReason;
    location: string;
}

// ==================== TRANSFORMATION FUNCTIONS ====================

/**
 * Flatten grouped histogram data (Record<applicationId, HistogramEntry[]>) 
 * into a single HistogramModel by aggregating counts across all applications.
 * 
 * The organization-level histogram APIs return data grouped by application ID.
 * This function aggregates the counts for each date/time bucket across all apps.
 */
export const flattenGroupedHistogramData = (
    groupedData: GroupedHistogramData | null | undefined
): HistogramModel | null => {
    if (!groupedData || typeof groupedData !== 'object') {
        return null;
    }

    const allEntries = Object.values(groupedData).flat();
    if (allEntries.length === 0) {
        return null;
    }

    // Aggregate counts by date key
    const aggregatedByDate: Record<string, { key: string; date: string; count: number }> = {};

    allEntries.forEach((entry: HistogramEntry) => {
        const dateKey = entry.key || entry.date;
        if (!aggregatedByDate[dateKey]) {
            aggregatedByDate[dateKey] = {
                key: entry.key,
                date: dateKey,
                count: 0
            };
        }
        aggregatedByDate[dateKey].count += entry.count;
    });

    // Convert to sorted array
    const data = Object.values(aggregatedByDate)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
        id: 'aggregated',
        filter: null as any,
        interval: null as any,
        data: data
    };
};

/**
 * Transform API AppBreakdown to dashboard format
 */
export const transformAppBreakdown = (apiApp: ApiAppBreakdown): DashboardAppBreakdown => ({
    appName: apiApp.applicationName,
    count: apiApp.count,
    english: apiApp.english,
    spanish: apiApp.spanish
});

/**
 * Flatten grouped histogram data, filtering to only include specified application IDs.
 * If applicationIds is empty or undefined, aggregates all apps (same as flattenGroupedHistogramData).
 */
export const flattenGroupedHistogramDataFiltered = (
    groupedData: GroupedHistogramData | null | undefined,
    applicationIds?: Set<string> | string[]
): HistogramModel | null => {
    if (!groupedData || typeof groupedData !== 'object') {
        return null;
    }

    // Convert to Set for efficient lookup
    // When applicationIds is undefined/null, include all apps (initial state before apps load)
    // When applicationIds is an empty Set/Array, return null (user deselected all)
    let appIdSet: Set<string> | null = null;
    if (applicationIds !== undefined && applicationIds !== null) {
        if (applicationIds instanceof Set) {
            if (applicationIds.size === 0) return null; // Empty filter = no data
            appIdSet = applicationIds;
        } else {
            if (applicationIds.length === 0) return null; // Empty filter = no data
            appIdSet = new Set(applicationIds);
        }
    }

    // Filter entries by applicationIds if provided
    const filteredEntries: HistogramEntry[] = [];
    for (const [appId, entries] of Object.entries(groupedData)) {
        if (!appIdSet || appIdSet.has(appId)) {
            filteredEntries.push(...entries);
        }
    }

    if (filteredEntries.length === 0) {
        return null;
    }

    // Aggregate counts by date key
    const aggregatedByDate: Record<string, { key: string; date: string; count: number }> = {};

    filteredEntries.forEach((entry: HistogramEntry) => {
        const dateKey = entry.key || entry.date;
        if (!aggregatedByDate[dateKey]) {
            aggregatedByDate[dateKey] = {
                key: entry.key,
                date: dateKey,
                count: 0
            };
        }
        aggregatedByDate[dateKey].count += entry.count;
    });

    // Convert to sorted array
    const data = Object.values(aggregatedByDate)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
        id: 'aggregated-filtered',
        filter: null as any,
        interval: null as any,
        data: data
    };
};

/**
 * Flatten grouped concurrent call histogram data using MAXIMUM instead of sum.
 * 
 * For concurrent calls, summing values across apps is incorrect because the histogram
 * shows peak concurrent calls for each time bucket. Since peaks at different apps may
 * not occur at exactly the same moment within a bucket, summing inflates the values.
 * 
 * This function takes the maximum concurrent call value across all apps for each time bucket,
 * which represents the peak concurrent calls observed at any single app during that period.
 * 
 * @param groupedData - Histogram data grouped by application ID
 * @param applicationIds - Optional filter for specific application IDs
 * @returns A flattened HistogramModel with max values per time bucket
 */
export const flattenConcurrentCallHistogramDataFiltered = (
    groupedData: GroupedHistogramData | null | undefined,
    applicationIds?: Set<string> | string[]
): HistogramModel | null => {
    if (!groupedData || typeof groupedData !== 'object') {
        return null;
    }

    // Convert to Set for efficient lookup
    // When applicationIds is undefined/null, include all apps (initial state before apps load)
    // When applicationIds is an empty Set/Array, return null (user deselected all)
    let appIdSet: Set<string> | null = null;
    if (applicationIds !== undefined && applicationIds !== null) {
        if (applicationIds instanceof Set) {
            if (applicationIds.size === 0) return null; // Empty filter = no data
            appIdSet = applicationIds;
        } else {
            if (applicationIds.length === 0) return null; // Empty filter = no data
            appIdSet = new Set(applicationIds);
        }
    }

    // Filter entries by applicationIds if provided
    const filteredEntries: HistogramEntry[] = [];
    for (const [appId, entries] of Object.entries(groupedData)) {
        if (!appIdSet || appIdSet.has(appId)) {
            filteredEntries.push(...entries);
        }
    }

    if (filteredEntries.length === 0) {
        return null;
    }

    // Aggregate by taking MAXIMUM count for each date key (not sum)
    const aggregatedByDate: Record<string, { key: string; date: string; count: number }> = {};

    filteredEntries.forEach((entry: HistogramEntry) => {
        const dateKey = entry.key || entry.date;
        if (!aggregatedByDate[dateKey]) {
            aggregatedByDate[dateKey] = {
                key: entry.key,
                date: dateKey,
                count: entry.count
            };
        } else {
            // Take the maximum instead of summing
            aggregatedByDate[dateKey].count = Math.max(aggregatedByDate[dateKey].count, entry.count);
        }
    });

    // Convert to sorted array
    const data = Object.values(aggregatedByDate)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    return {
        id: 'concurrent-max-filtered',
        filter: null as any,
        interval: null as any,
        data: data
    };
};

const hasExplicitAnalyticsTime = (dateStr: string, boundary: 'start' | 'end'): boolean => {
    if (!dateStr || moment(dateStr, ANALYTICS_DATE_ONLY_FORMAT, true).isValid()) {
        return false;
    }

    const parsed = parseAnalyticsDate(dateStr);
    if (!parsed.isValid()) {
        return false;
    }

    if (boundary === 'start') {
        return parsed.hours() !== 0 || parsed.minutes() !== 0 || parsed.seconds() !== 0 || parsed.milliseconds() !== 0;
    }

    return parsed.hours() !== 23 || parsed.minutes() !== 59 || parsed.seconds() !== 59 || parsed.milliseconds() !== 999;
};

const roundToClosestHour = (date: moment.Moment): moment.Moment => {
    const rounded = date.clone().startOf('hour');
    if (date.minutes() >= 30 || date.seconds() > 0 || date.milliseconds() > 0) {
        rounded.add(1, 'hour');
    }
    return rounded;
};

const getHistogramPointDate = (point: DatedUsageModel): string => {
    return ((point as any).key || point.date) as string;
};

const isHourlyHistogramData = (data: DatedUsageModel[]): boolean => {
    if (!data || data.length < 2) return false;

    for (let i = 1; i < data.length; i++) {
        const previous = parseAnalyticsDate(getHistogramPointDate(data[i - 1]));
        const current = parseAnalyticsDate(getHistogramPointDate(data[i]));
        if (!previous.isValid() || !current.isValid()) continue;

        const diffMinutes = Math.abs(current.diff(previous, 'minutes'));
        if (diffMinutes > 0) {
            return diffMinutes >= 45 && diffMinutes <= 75;
        }
    }

    return false;
};

export const trimHistogramToSelectedHourRange = (
    histogram: HistogramModel | null,
    startDate: string,
    endDate: string
): HistogramModel | null => {
    if (!histogram?.data?.length) {
        return histogram;
    }

    const shouldTrim = hasExplicitAnalyticsTime(startDate, 'start') || hasExplicitAnalyticsTime(endDate, 'end');
    if (!shouldTrim) {
        return histogram;
    }

    const start = roundToClosestHour(parseAnalyticsDate(startDate));
    const end = roundToClosestHour(parseAnalyticsDate(endDate));
    if (!start.isValid() || !end.isValid()) {
        return histogram;
    }

    if (!isHourlyHistogramData(histogram.data)) {
        return histogram;
    }

    const rangeEnd = end.isBefore(start) ? start.clone() : end;
    const startIndex = histogram.data.findIndex(point => {
        const pointMoment = parseAnalyticsDate(getHistogramPointDate(point));
        if (!pointMoment.isValid()) return false;

        const bucketHour = pointMoment.clone().startOf('hour');
        return bucketHour.isSameOrAfter(start);
    });

    if (startIndex === -1) {
        return { ...histogram, data: [] };
    }

    let endIndex = startIndex;
    for (let i = startIndex; i < histogram.data.length; i++) {
        const pointMoment = parseAnalyticsDate(getHistogramPointDate(histogram.data[i]));
        if (!pointMoment.isValid()) continue;

        const bucketHour = pointMoment.clone().startOf('hour');
        if (bucketHour.isSameOrBefore(rangeEnd)) {
            endIndex = i;
        } else {
            break;
        }
    }

    return { ...histogram, data: histogram.data.slice(startIndex, endIndex + 1) };
};

/**
 * Aggregate TopLevelMetrics from per-app data, filtering to only include specified application IDs.
 * If applicationIds is empty or undefined, aggregates all apps.
 * If spanishOnly is true, only aggregate Spanish metrics.
 */
export const aggregateTopLevelMetricsFiltered = (
    perAppMetrics: Record<string, TopLevelMetrics> | null | undefined,
    applicationIds?: Set<string> | string[],
    spanishOnly?: boolean
): TopLevelMetrics | null => {
    if (!perAppMetrics || typeof perAppMetrics !== 'object') {
        return null;
    }

    // Convert to Set for efficient lookup
    // When applicationIds is undefined/null, include all apps (initial state before apps load)
    // When applicationIds is an empty Set/Array, return null (user deselected all)
    let appIdSet: Set<string> | null = null;
    if (applicationIds !== undefined && applicationIds !== null) {
        if (applicationIds instanceof Set) {
            if (applicationIds.size === 0) return null; // Empty filter = no data
            appIdSet = applicationIds;
        } else {
            if (applicationIds.length === 0) return null; // Empty filter = no data
            appIdSet = new Set(applicationIds);
        }
    }

    const aggregatedMetrics: TopLevelMetrics = {
        laborMinutesSaved: 0,
        laborHoursSaved: 0,
        totalCalls: 0,
        containedCalls: 0,
        containableCalls: 0,
        offHoursCalls: 0,
        offHoursScheduledAppts: 0,
        newPatientBookings: 0,
        existingPatientBookings: 0,
        totalBookings: 0,
        orderAttempts: 0,
        ordersPlaced: 0,
        upsellAttempts: 0,
        upsellSuccesses: 0,
        questionsAnswered: 0,
        averageOrderTotal: 0,
        laborHoursSavedApps: [],
        offHoursApps: [],
        offHoursScheduledApptsApps: [],
        newPatientBookingsApps: [],
        ticketApps: [],
        upsellApps: [],
        questionsApps: []
    };

    // Track weighted average for averageOrderTotal
    let totalOrdersForAverage = 0;
    let weightedOrderTotalSum = 0;

    for (const [appId, metrics] of Object.entries(perAppMetrics)) {
        // Skip if filtering and this app is not selected
        if (appIdSet && !appIdSet.has(appId)) {
            continue;
        }

        if (metrics) {
            // When spanishOnly is true, only aggregate Spanish-specific metrics
            if (spanishOnly) {
                aggregatedMetrics.laborMinutesSaved += (metrics as any).laborMinutesSavedSpanish ?? 0;
                aggregatedMetrics.totalCalls += (metrics as any).totalEngagementCountSpanish ?? 0;
                aggregatedMetrics.containedCalls += (metrics as any).totalContainedCountSpanish ?? 0;
                aggregatedMetrics.containableCalls +=
                    ((metrics as any).totalScheduleAttemptsSpanish ?? 0) +
                    ((metrics as any).totalRescheduleAttemptsSpanish ?? 0) +
                    ((metrics as any).totalCancellationAttemptsSpanish ?? 0) +
                    ((metrics as any).totalCheckNextAppointmentAttemptsSpanish ?? 0);
                aggregatedMetrics.newPatientBookings += (metrics as any).newPatientBookedSpanish ?? 0;
            } else {
                aggregatedMetrics.laborMinutesSaved += metrics.laborMinutesSaved ?? 0;
                aggregatedMetrics.totalCalls += (metrics as any).totalEngagementCount ?? metrics.totalCalls ?? 0;
                aggregatedMetrics.containedCalls += ((metrics as any).totalContainedCountEnglish ?? 0) + ((metrics as any).totalContainedCountSpanish ?? 0);
                // Containable calls = total scheduling attempts (schedule + reschedule + cancel + check appt)
                aggregatedMetrics.containableCalls +=
                    ((metrics as any).totalScheduleAttemptsEnglish ?? 0) + ((metrics as any).totalScheduleAttemptsSpanish ?? 0) +
                    ((metrics as any).totalRescheduleAttemptsEnglish ?? 0) + ((metrics as any).totalRescheduleAttemptsSpanish ?? 0) +
                    ((metrics as any).totalCancellationAttemptsEnglish ?? 0) + ((metrics as any).totalCancellationAttemptsSpanish ?? 0) +
                    ((metrics as any).totalCheckNextAppointmentAttemptsEnglish ?? 0) + ((metrics as any).totalCheckNextAppointmentAttemptsSpanish ?? 0);
                aggregatedMetrics.newPatientBookings += ((metrics as any).newPatientBookedEnglish ?? 0) + ((metrics as any).newPatientBookedSpanish ?? 0);
            }
            aggregatedMetrics.offHoursCalls += metrics.offHoursCalls ?? 0;
            aggregatedMetrics.offHoursScheduledAppts += metrics.offHoursScheduledAppts ?? 0;

            // Food ordering metrics — use Spanish-specific API fields when spanishOnly
            if (spanishOnly) {
                aggregatedMetrics.ordersPlaced += metrics.totalOrdersCompletedSpanish ?? (metrics as any).totalOrdersCompleted ?? metrics.ordersPlaced ?? 0;
                aggregatedMetrics.upsellAttempts += metrics.totalUpsellAttemptsSpanish ?? (metrics as any).totalUpsellAttempts ?? metrics.upsellAttempts ?? 0;
                aggregatedMetrics.upsellSuccesses += metrics.totalUpsellsCompletedSpanish ?? (metrics as any).totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
                aggregatedMetrics.questionsAnswered += metrics.totalQuestionsAnsweredSpanish ?? (metrics as any).totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;
            } else {
                aggregatedMetrics.orderAttempts += (metrics as any).totalOrderAttempts ?? metrics.orderAttempts ?? 0;
                aggregatedMetrics.ordersPlaced += (metrics as any).totalOrdersCompleted ?? metrics.ordersPlaced ?? 0;
                aggregatedMetrics.upsellAttempts += (metrics as any).totalUpsellAttempts ?? metrics.upsellAttempts ?? 0;
                aggregatedMetrics.upsellSuccesses += (metrics as any).totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
                aggregatedMetrics.questionsAnswered += (metrics as any).totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;
            }

            // Aggregate averageOrderTotal as weighted average
            // Use Spanish-specific ticket size when spanishOnly
            const appOrderTotal = spanishOnly
                ? (metrics.averageTicketSizeSpanish ?? (metrics as any).averageTicketSize ?? metrics.averageOrderTotal ?? 0)
                : ((metrics as any).averageTicketSize ?? metrics.averageOrderTotal ?? (metrics as any).AverageOrderTotal ?? (metrics as any).totalAverageOrderValue ?? 0);
            const appOrderCount = spanishOnly
                ? (metrics.totalOrdersCompletedSpanish ?? (metrics as any).totalOrdersCompleted ?? metrics.ordersPlaced ?? 0)
                : ((metrics as any).totalOrdersCompleted ?? metrics.ordersPlaced ?? 0);
            if (appOrderTotal > 0) {
                if (appOrderCount > 0) {
                    weightedOrderTotalSum += appOrderTotal * appOrderCount;
                    totalOrdersForAverage += appOrderCount;
                } else {
                    weightedOrderTotalSum += appOrderTotal;
                    totalOrdersForAverage += 1;
                }
            }

            // Add app breakdown for laborHoursSaved
            const laborMinutesEnglish = (metrics as any).laborMinutesSavedEnglish ?? 0;
            const laborMinutesSpanish = (metrics as any).laborMinutesSavedSpanish ?? 0;
            const laborMinutes = spanishOnly
                ? laborMinutesSpanish
                : ((laborMinutesEnglish + laborMinutesSpanish) || (metrics.laborMinutesSaved ?? 0));
            if (laborMinutes > 0) {
                aggregatedMetrics.laborHoursSavedApps.push({
                    applicationId: appId,
                    applicationName: appId,
                    count: laborMinutes / 60,
                    english: spanishOnly ? 0 : laborMinutesEnglish / 60,
                    spanish: laborMinutesSpanish / 60
                });
            }

            // Add app breakdown for newPatientBookings
            const englishCount = (metrics as any).newPatientBookedEnglish ?? 0;
            const spanishCount = (metrics as any).newPatientBookedSpanish ?? 0;
            const totalCount = spanishOnly ? spanishCount : (englishCount + spanishCount);
            if (totalCount > 0) {
                aggregatedMetrics.newPatientBookingsApps.push({
                    applicationId: appId,
                    applicationName: appId,
                    count: totalCount,
                    english: spanishOnly ? 0 : englishCount,
                    spanish: spanishCount
                });
            }

            // Add app breakdown for ticketApps
            if (appOrderTotal > 0) {
                aggregatedMetrics.ticketApps.push({
                    applicationId: appId,
                    applicationName: appId,
                    count: appOrderTotal
                });
            }

            // Add app breakdown for upsellApps
            const upsellSuccessCount = (metrics as any).totalUpsellsCompleted ?? metrics.upsellSuccesses ?? 0;
            if (upsellSuccessCount > 0) {
                aggregatedMetrics.upsellApps.push({
                    applicationId: appId,
                    applicationName: appId,
                    count: upsellSuccessCount
                });
            }

            // Add app breakdown for questionsApps
            const questionsCount = (metrics as any).totalQuestionsAnswered ?? metrics.questionsAnswered ?? 0;
            if (questionsCount > 0) {
                aggregatedMetrics.questionsApps.push({
                    applicationId: appId,
                    applicationName: appId,
                    count: questionsCount
                });
            }
        }
    }

    // Convert laborMinutesSaved to laborHoursSaved
    aggregatedMetrics.laborHoursSaved = aggregatedMetrics.laborMinutesSaved / 60;

    // Calculate weighted average for averageOrderTotal
    aggregatedMetrics.averageOrderTotal = totalOrdersForAverage > 0
        ? weightedOrderTotalSum / totalOrdersForAverage
        : 0;

    return aggregatedMetrics;
};

/**
 * Filter OrganizationEngagement array to only include engagements from selected application IDs.
 * If applicationIds is empty or undefined, returns all engagements.
 * 
 * @param engagements - Array of engagement records from the API
 * @param applicationIds - Set or array of application IDs to include
 * @returns Filtered array of engagements
 */
export const filterEngagementsByAppIds = (
    engagements: OrganizationEngagement[] | null | undefined,
    applicationIds?: Set<string> | string[]
): OrganizationEngagement[] => {
    return filterEngagementsByEntityIds(engagements, applicationIds, 'applicationId');
};

export const filterEngagementsByOrganizationIds = (
    engagements: OrganizationEngagement[] | null | undefined,
    organizationIds?: Set<string> | string[]
): OrganizationEngagement[] => filterEngagementsByEntityIds(engagements, organizationIds, 'organizationId');

/**
 * Filter TransferReasonsApiResponse to only include data from selected application IDs.
 * If applicationIds is empty or undefined, returns all data unchanged.
 * 
 * TransferReasonsApiResponse format: Record<applicationId, Record<reason, count>>
 * 
 * @param transferReasons - The API response for transfer reasons (already keyed by applicationId)
 * @param applicationIds - Set or array of application IDs to include
 * @returns Filtered transfer reasons response
 */
export const filterTransferReasonsByAppIds = (
    transferReasons: TransferReasonsApiResponse | null | undefined,
    applicationIds?: Set<string> | string[]
): TransferReasonsApiResponse | null => {
    return filterGroupedCountsByEntityIds(transferReasons, applicationIds);
};

export const filterTransferReasonsByOrganizationIds = (
    transferReasons: TransferReasonsApiResponse | null | undefined,
    organizationIds?: Set<string> | string[]
): TransferReasonsApiResponse | null => filterGroupedCountsByEntityIds(transferReasons, organizationIds);

/**
 * Transform array of API AppBreakdowns to dashboard format
 */
export const transformAppBreakdowns = (apiApps?: ApiAppBreakdown[]): DashboardAppBreakdown[] => {
    if (!apiApps) return [];
    return apiApps.map(transformAppBreakdown);
};

/**
 * Transform TopLevelMetrics API response to healthcare dashboard format
 */
export const transformToHealthcareMetrics = (
    metrics: TopLevelMetrics | null
): DashboardHealthcareMetrics => {
    if (!metrics) {
        return {
            laborHoursSaved: 0,
            offHoursCalls: 0,
            offHoursScheduledAppts: 0,
            containedCallsCount: 0,
            containableCallsCount: 0,
            totalEngagements: 0,
            newPatientBookings: 0,
            laborHoursSavedApps: [],
            offHoursApps: [],
            offHoursScheduledApptsApps: [],
            newPatientBookingsApps: []
        };
    }

    // Convert laborMinutesSaved from API to hours (or use laborHoursSaved if already computed)
    const laborHoursSaved = metrics.laborMinutesSaved !== undefined
        ? metrics.laborMinutesSaved / 60
        : (metrics.laborHoursSaved ?? 0);

    return {
        laborHoursSaved,
        offHoursCalls: metrics.offHoursCalls ?? 0,
        offHoursScheduledAppts: metrics.offHoursScheduledAppts ?? 0,
        containedCallsCount: metrics.containedCalls ?? 0,
        containableCallsCount: metrics.containableCalls ?? 0,
        totalEngagements: metrics.totalCalls ?? 0,
        newPatientBookings: metrics.newPatientBookings ?? 0,
        laborHoursSavedApps: transformAppBreakdowns(metrics.laborHoursSavedApps),
        offHoursApps: transformAppBreakdowns(metrics.offHoursApps),
        offHoursScheduledApptsApps: transformAppBreakdowns(metrics.offHoursScheduledApptsApps),
        newPatientBookingsApps: transformAppBreakdowns(metrics.newPatientBookingsApps)
    };
};

/**
 * Transform TopLevelMetrics API response to food ordering dashboard format
 */
export const transformToFoodOrderingMetrics = (
    metrics: TopLevelMetrics | null
): DashboardFoodOrderingMetrics => {
    if (!metrics) {
        return {
            upsellSuccessRate: 0,
            successfulUpsells: 0,
            upsellAttempts: 0,
            averageTicketSize: 0,
            totalSold: 0,
            ordersPlaced: 0,
            questionsAnswered: 0,
            laborHoursSaved: 0,
            upsoldRevenueGenerated: 0,
            upsellApps: [],
            ticketApps: [],
            questionsApps: [],
            laborHoursSavedApps: []
        };
    }

    const upsellAttempts = metrics.upsellAttempts ?? 0;
    const upsellSuccesses = metrics.upsellSuccesses ?? 0;
    const upsellRate = upsellAttempts > 0 ? (upsellSuccesses / upsellAttempts) * 100 : 0;

    // Convert laborMinutesSaved from API to hours (or use laborHoursSaved if already computed)
    const laborHoursSaved = metrics.laborMinutesSaved != null
        ? metrics.laborMinutesSaved / 60
        : (metrics.laborHoursSaved ?? 0);

    // Calculate total sold: use totalRevenue if available, otherwise compute from ordersPlaced * averageOrderTotal
    const averageTicket = metrics.averageOrderTotal ?? (metrics as any).averageTicketSize ?? (metrics as any).average_ticket_total ?? 0;
    const totalSold = metrics?.totalRevenue ?? ((metrics.ordersPlaced ?? 0) * averageTicket);

    return {
        upsellSuccessRate: Math.round(upsellRate * 10) / 10,
        successfulUpsells: upsellSuccesses,
        upsellAttempts: upsellAttempts,
        averageTicketSize: metrics.averageOrderTotal ?? 0,
        totalSold,
        ordersPlaced: metrics.ordersPlaced ?? 0,
        questionsAnswered: metrics.questionsAnswered ?? 0,
        laborHoursSaved,
        upsoldRevenueGenerated: 0,
        upsellApps: transformAppBreakdowns(metrics.upsellApps),
        ticketApps: transformAppBreakdowns(metrics.ticketApps),
        questionsApps: transformAppBreakdowns(metrics.questionsApps),
        laborHoursSavedApps: transformAppBreakdowns(metrics.laborHoursSavedApps)
    };
};

/**
 * Transform HistogramModel API response to chart-compatible dataset format
 * Automatically detects hourly vs daily data and formats x-axis labels appropriately
 */
export const transformHistogramToDataset = (
    histogram: HistogramModel | null,
    datasetId: string,
    color: string
): HistogramDataset => {
    if (!histogram || !histogram.data) {
        return { id: datasetId, color, data: [] };
    }

    // Detect if this is hourly data by checking if multiple points have the same date
    // Hourly data will have 24 points per day with different hours
    const isHourlyData = histogram.data.length > 1 && (() => {
        const firstDate = moment(histogram.data[0].date).format('YYYY-MM-DD');
        const secondDate = moment(histogram.data[1].date).format('YYYY-MM-DD');
        return firstDate === secondDate;
    })();

    // Traverse backwards and trim future 0-count buckets
    const now = moment();
    let lastValidIndex = histogram.data.length - 1;
    while (lastValidIndex >= 0) {
        const point = histogram.data[lastValidIndex];
        const pointMoment = moment(point.date);
        const isFuture = isHourlyData
            ? pointMoment.isAfter(now)
            : pointMoment.startOf('day').isAfter(now.clone().startOf('day'));

        if (isFuture && point.count === 0) {
            lastValidIndex--;
        } else {
            break; // Stop trimming if not in future OR has data
        }
    }
    const filteredData = histogram.data.slice(0, lastValidIndex + 1);

    const dataPoints: HistogramDataPoint[] = filteredData.map((point: DatedUsageModel) => {
        // For hourly data, format as hour (e.g., "2 AM", "12 PM")
        // For daily or longer intervals, format as date (e.g., "Feb 4")
        let xLabel = moment(point.date).format('MMM D');
        if (isHourlyData) {
            const hasMultipleDays = histogram.data.length > 0 && (() => {
                const startDay = moment(histogram.data[0].date).startOf('day');
                const endDay = moment(histogram.data[histogram.data.length - 1].date).startOf('day');
                return endDay.diff(startDay, 'days') > 0;
            })();
            xLabel = hasMultipleDays
                ? moment(point.date).format('M/D h A') // e.g. "6/21 2 AM"
                : moment(point.date).format('h A');    // e.g. "2 AM"
        }

        return {
            x: xLabel,
            y: point.count,
            color: color,
            fullDate: point.date
        };
    });

    return {
        id: datasetId,
        color,
        data: dataPoints
    };
};

/**
 * Transform multiple histograms into the multi-line chart format
 * Used for "All Conversations", "Containable Conversations", "Contained Conversations" lines
 */
export const transformHistogramsToMultiLineData = (
    allCallsHistogram: HistogramModel | null,
    containableCallsHistogram: HistogramModel | null,
    containedCallsHistogram: HistogramModel | null,
    colors: { allCalls: string; containable: string; contained: string }
): HistogramDataset[] => {
    const datasets: HistogramDataset[] = [];

    // Only add datasets that have data
    if (allCallsHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(allCallsHistogram, 'All Conversations', colors.allCalls));
    }
    if (containableCallsHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(containableCallsHistogram, 'Containable Conversations', colors.containable));
    }
    if (containedCallsHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(containedCallsHistogram, 'Contained Conversations', colors.contained));
    }

    return datasets;
};

/**
 * Transform Spanish calls histograms into the multi-line chart format
 * Used for "Spanish Calls" view with three lines: All Containable, Spanish Containable, Spanish Contained
 */
export const transformSpanishHistogramsToMultiLineData = (
    spanishCallsHistogram: HistogramModel | null,
    spanishContainableHistogram: HistogramModel | null,
    spanishContainedHistogram: HistogramModel | null,
    colors: { spanishCalls: string; spanishContainable: string; spanishContained: string }
): HistogramDataset[] => {
    const datasets: HistogramDataset[] = [];

    // Only add datasets that have data
    if (spanishCallsHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(spanishCallsHistogram, 'All Spanish Calls', colors.spanishCalls));
    }
    if (spanishContainableHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(spanishContainableHistogram, 'Spanish Containable Conversations', colors.spanishContainable));
    }
    if (spanishContainedHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(spanishContainedHistogram, 'Spanish Contained Conversations', colors.spanishContained));
    }

    return datasets;
};

/**
 * Transform order volume histograms into the multi-line chart format
 * Used for "Order Calls" view with three lines: All Conversations, Order Attempted, Order Placed
 */
export const transformOrderVolumeToMultiLineData = (
    allCallsHistogram: HistogramModel | null,
    orderAttemptedHistogram: HistogramModel | null,
    orderPlacedHistogram: HistogramModel | null,
    colors: { allCalls: string; orderAttempted: string; orderPlaced: string }
): HistogramDataset[] => {
    const datasets: HistogramDataset[] = [];

    // Only add datasets that have data
    if (allCallsHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(allCallsHistogram, 'All Conversations', colors.allCalls));
    }
    if (orderAttemptedHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(orderAttemptedHistogram, 'Order Attempted', colors.orderAttempted));
    }
    if (orderPlacedHistogram?.data?.length) {
        datasets.push(transformHistogramToDataset(orderPlacedHistogram, 'Order Placed', colors.orderPlaced));
    }

    return datasets;
};

/**
 * Transform average call volume histogram for "Average Calls by Hour" view
 * Formats x-axis labels as hour of day (12AM, 1AM, etc.)
 */
export const transformAverageByHourHistogramToDataset = (
    histogram: HistogramModel | null,
    datasetId: string,
    color: string
): HistogramDataset => {
    if (!histogram || !histogram.data) {
        return { id: datasetId, color, data: [] };
    }

    const dataPoints: HistogramDataPoint[] = histogram.data.map((point: DatedUsageModel) => {
        const hour = moment(point.date).hour();
        const hourLabel = hour === 0 ? '12AM' : hour === 12 ? '12PM' : hour < 12 ? `${hour}AM` : `${hour - 12}PM`;
        return {
            x: hourLabel,
            y: point.count,
            color: color,
            fullDate: point.date
        };
    });

    return {
        id: datasetId,
        color,
        data: dataPoints
    };
};

/**
 * Transform average call volume histogram for "Average Calls by Day of Week" view
 * Formats x-axis labels as day names (Mon, Tue, etc.)
 */
export const transformAverageByDayHistogramToDataset = (
    histogram: HistogramModel | null,
    datasetId: string,
    color: string
): HistogramDataset => {
    if (!histogram || !histogram.data) {
        return { id: datasetId, color, data: [] };
    }

    const dataPoints: HistogramDataPoint[] = histogram.data.map((point: DatedUsageModel) => ({
        x: moment(point.date).format('ddd'),
        y: point.count,
        color: color,
        fullDate: point.date
    }));

    return {
        id: datasetId,
        color,
        data: dataPoints
    };
};

/**
 * Transform concurrent calls histogram into chart format
 * Used for "Concurrent Calls" view
 * Automatically detects hourly vs daily data and formats x-axis labels appropriately
 */
export const transformConcurrentHistogramToDataset = (
    histogram: HistogramModel | null,
    datasetId: string,
    color: string
): HistogramDataset => {
    if (!histogram || !histogram.data) {
        return { id: datasetId, color, data: [] };
    }

    // Detect if this is hourly data by checking if multiple points have the same date
    const isHourlyData = histogram.data.length > 1 && (() => {
        const firstDate = moment(histogram.data[0].date).format('YYYY-MM-DD');
        const secondDate = moment(histogram.data[1].date).format('YYYY-MM-DD');
        return firstDate === secondDate;
    })();

    // Traverse backwards and trim future 0-count buckets
    const now = moment();
    let lastValidIndex = histogram.data.length - 1;
    while (lastValidIndex >= 0) {
        const point = histogram.data[lastValidIndex];
        const pointMoment = moment(point.date);
        const isFuture = isHourlyData
            ? pointMoment.isAfter(now)
            : pointMoment.startOf('day').isAfter(now.clone().startOf('day'));

        if (isFuture && point.count === 0) {
            lastValidIndex--;
        } else {
            break; // Stop trimming if not in future OR has data
        }
    }
    const filteredData = histogram.data.slice(0, lastValidIndex + 1);

    const dataPoints: HistogramDataPoint[] = filteredData.map((point: DatedUsageModel) => {
        let xLabel = moment(point.date).format('MMM D');
        if (isHourlyData) {
            const hasMultipleDays = histogram.data.length > 0 && (() => {
                const startDay = moment(histogram.data[0].date).startOf('day');
                const endDay = moment(histogram.data[histogram.data.length - 1].date).startOf('day');
                return endDay.diff(startDay, 'days') > 0;
            })();
            xLabel = hasMultipleDays
                ? moment(point.date).format('M/D h A') // e.g. "6/21 2 AM"
                : moment(point.date).format('h A');    // e.g. "2 AM"
        }

        return {
            x: xLabel,
            y: point.count,
            color: color,
            fullDate: point.date
        };
    });

    return {
        id: datasetId,
        color,
        data: dataPoints
    };
};

/**
 * Transform TransferReasonsResponse API response to chart-compatible format
 */
export const transformTransferReasonsToChartData = (
    transferReasons: TransferReasonsResponse | null
): TransferCategoryData[] => {
    if (!transferReasons) {
        return [];
    }

    const plannedCategory: TransferCategoryData = { category: 'Planned' };
    const unplannedCategory: TransferCategoryData = { category: 'Unplanned' };

    // Transform planned transfer reasons
    transferReasons.planned?.forEach((reason: ApiTransferReasonData) => {
        const reasonKey = reason.reason as TransferReason;
        // Skip Immediate Transfers - they are not containable
        if (reasonKey === TransferReason.IMMEDIATE_TRANSFER) return;
        plannedCategory[reasonKey] = {
            value: reason.count,
            apps: reason.apps?.map((app: ApiAppBreakdown) => ({
                appName: app.applicationName,
                count: app.count,
                english: app.english,
                spanish: app.spanish
            })) || []
        } as ChartTransferReasonData;
    });

    // Transform unplanned transfer reasons
    transferReasons.unplanned?.forEach((reason: ApiTransferReasonData) => {
        const reasonKey = reason.reason as TransferReason;
        // Skip Immediate Transfers - they are not containable
        if (reasonKey === TransferReason.IMMEDIATE_TRANSFER) return;
        unplannedCategory[reasonKey] = {
            value: reason.count,
            apps: reason.apps?.map((app: ApiAppBreakdown) => ({
                appName: app.applicationName,
                count: app.count,
                english: app.english,
                spanish: app.spanish
            })) || []
        } as ChartTransferReasonData;
    });

    return [plannedCategory, unplannedCategory];
};

/**
 * Transform legacy TransferReason[] API response to chart-compatible format
 * This works with the current API endpoint that returns a flat array with isPlanned flag
 */
export const transformLegacyTransferReasonsToChartData = (
    transferReasons: LegacyTransferReason[] | null
): TransferCategoryData[] => {
    if (!transferReasons || transferReasons.length === 0) {
        return [];
    }

    const plannedCategory: TransferCategoryData = { category: 'Planned' };
    const unplannedCategory: TransferCategoryData = { category: 'Unplanned' };

    // Group transfer reasons by planned/unplanned
    transferReasons.forEach((reason: LegacyTransferReason) => {
        if (!reason.reason) return;

        const reasonKey = reason.reason as TransferReason;
        // Skip Immediate Transfers - they are not containable
        if (reasonKey === TransferReason.IMMEDIATE_TRANSFER) return;

        const targetCategory = reason.isPlanned ? plannedCategory : unplannedCategory;

        targetCategory[reasonKey] = {
            value: reason.count || 0,
            apps: reason.apps?.map((app: ApiAppBreakdown) => ({
                appName: app.applicationName,
                count: app.count,
                english: app.english,
                spanish: app.spanish
            })) || []
        } as ChartTransferReasonData;
    });

    return [plannedCategory, unplannedCategory];
};

/**
 * Application name lookup map type
 * Maps applicationId to application name
 */
export type AppNameMap = Record<string, string>;
export type EntityNameMap = Record<string, string>;

const getEntityId = (engagement: OrganizationEngagement, groupBy: 'applicationId' | 'organizationId'): string => {
    const value = groupBy === 'organizationId' ? engagement.organizationId : engagement.applicationId;
    return value || 'unknown';
};

export const filterEngagementsByEntityIds = (
    engagements: OrganizationEngagement[] | null | undefined,
    entityIds?: Set<string> | string[],
    groupBy: 'applicationId' | 'organizationId' = 'applicationId'
): OrganizationEngagement[] => {
    if (!engagements || engagements.length === 0) {
        return [];
    }

    if (entityIds === undefined || entityIds === null) {
        return engagements;
    }

    let entityIdSet: Set<string>;
    if (entityIds instanceof Set) {
        if (entityIds.size === 0) return [];
        entityIdSet = entityIds;
    } else {
        if (entityIds.length === 0) return [];
        entityIdSet = new Set(entityIds);
    }

    return engagements.filter(engagement => entityIdSet.has(getEntityId(engagement, groupBy)));
};

export const filterGroupedCountsByEntityIds = <T extends Record<string, any>>(
    groupedData: T | null | undefined,
    entityIds?: Set<string> | string[]
): T | null => {
    if (!groupedData || typeof groupedData !== 'object') {
        return null;
    }

    if (entityIds === undefined || entityIds === null) {
        return groupedData;
    }

    let entityIdSet: Set<string>;
    if (entityIds instanceof Set) {
        if (entityIds.size === 0) return null;
        entityIdSet = entityIds;
    } else {
        if (entityIds.length === 0) return null;
        entityIdSet = new Set(entityIds);
    }

    const filtered = {} as T;
    Object.entries(groupedData).forEach(([entityId, value]) => {
        if (entityIdSet.has(entityId)) {
            filtered[entityId as keyof T] = value;
        }
    });

    return Object.keys(filtered).length > 0 ? filtered : null;
};

export const parseAnalyticsDate = (
    dateStr: string,
    fallback?: moment.Moment
): moment.Moment => {
    if (!dateStr) {
        return fallback ? fallback.clone() : moment.invalid();
    }

    const isoParsed = moment(dateStr, moment.ISO_8601, true);
    if (isoParsed.isValid()) {
        return isoParsed;
    }

    const strictParsed = moment(dateStr, ANALYTICS_INPUT_DATE_FORMATS, true);
    if (strictParsed.isValid()) {
        return strictParsed;
    }

    return fallback ? fallback.clone() : moment.invalid();
};

export const normalizeEngagementAnalyticsFilter = <T extends { startDate: string; endDate: string; timezone?: string }>(
    filter: T,
    options?: {
        includeOffset?: boolean;
        timezone?: string | undefined;
    }
): T => {
    const startMoment = parseAnalyticsDate(filter.startDate);
    const endMoment = parseAnalyticsDate(filter.endDate);

    if (!startMoment.isValid() || !endMoment.isValid()) {
        throw new Error(`Invalid analytics date filter. startDate=${filter.startDate}, endDate=${filter.endDate}`);
    }

    const includeOffset = options?.includeOffset !== false;
    const dateFormat = includeOffset ? ANALYTICS_API_DATE_FORMAT : ANALYTICS_API_DATE_FORMAT_NO_OFFSET;

    return {
        ...filter,
        startDate: startMoment.clone().startOf('day').format(dateFormat),
        endDate: endMoment.clone().endOf('day').format(dateFormat),
        timezone: options?.timezone ?? filter.timezone
    };
};

const hasExplicitTimezoneOffset = (dateStr: string): boolean => /(?:Z|[+-]\d{2}:?\d{2})$/i.test(dateStr);

export const parseAnalyticsDateInTimezone = (
    dateStr: string,
    timezone?: string,
    fallback?: moment.Moment
): moment.Moment => {
    if (!dateStr) {
        return fallback ? fallback.clone() : moment.invalid();
    }

    if (timezone && !hasExplicitTimezoneOffset(dateStr)) {
        const zonedParsed = moment.tz(dateStr, ANALYTICS_INPUT_DATE_FORMATS_NO_OFFSET, true, timezone);
        if (zonedParsed.isValid()) {
            return zonedParsed;
        }
    }

    return parseAnalyticsDate(dateStr, fallback);
};

export const normalizeEngagementAnalyticsFilterToUtc = <T extends { startDate: string; endDate: string; timezone?: string }>(
    filter: T,
    options?: {
        includeOffset?: boolean;
        timezone?: string | undefined;
    }
): T => {
    const timezone = options?.timezone ?? filter.timezone;
    const startMoment = parseAnalyticsDateInTimezone(filter.startDate, timezone);
    const endMoment = parseAnalyticsDateInTimezone(filter.endDate, timezone);

    if (!startMoment.isValid() || !endMoment.isValid()) {
        throw new Error(`Invalid analytics date filter. startDate=${filter.startDate}, endDate=${filter.endDate}, timezone=${timezone}`);
    }

    const includeOffset = options?.includeOffset !== false;
    const isDateOnly = (dateStr: string): boolean => moment(dateStr, ANALYTICS_DATE_ONLY_FORMAT, true).isValid();
    const normalizedStart = isDateOnly(filter.startDate) ? startMoment.clone().startOf('day') : startMoment.clone();
    const normalizedEnd = isDateOnly(filter.endDate) ? endMoment.clone().endOf('day') : endMoment.clone();

    return {
        ...filter,
        startDate: includeOffset ? normalizedStart.utc().toISOString() : normalizedStart.utc().format(ANALYTICS_API_DATE_FORMAT_NO_OFFSET),
        endDate: includeOffset ? normalizedEnd.utc().toISOString() : normalizedEnd.utc().format(ANALYTICS_API_DATE_FORMAT_NO_OFFSET),
        timezone
    };
};

export const normalizeAnalyticsRequestDateFilter = <T extends { startDate?: string; endDate?: string }>(filter: T): T => {
    const normalizedFilter = { ...filter };

    if (filter.startDate) {
        const startMoment = parseAnalyticsDate(filter.startDate);
        if (!startMoment.isValid()) {
            throw new Error(`Invalid analytics request startDate=${filter.startDate}`);
        }
        normalizedFilter.startDate = startMoment.toISOString();
    }

    if (filter.endDate) {
        const endMoment = parseAnalyticsDate(filter.endDate);
        if (!endMoment.isValid()) {
            throw new Error(`Invalid analytics request endDate=${filter.endDate}`);
        }
        normalizedFilter.endDate = endMoment.toISOString();
    }

    return normalizedFilter;
};

/**
 * Transform new TransferReasonsApiResponse format to chart-compatible format
 * API returns: { applicationId: { "REASON": count, ... }, ... }
 * Chart expects: [{ category: 'Planned', REASON: { value, apps } }, { category: 'Unplanned', ... }]
 * 
 * @param apiResponse - The API response containing transfer reasons grouped by application ID
 * @param appNameMap - Optional map of applicationId to application name for resolving app names
 */
export const transformTransferReasonsApiToChartData = (
    apiResponse: TransferReasonsApiResponse | null,
    appNameMap?: AppNameMap
): TransferCategoryData[] => {
    if (!apiResponse || Object.keys(apiResponse).length === 0) {
        return [];
    }

    const plannedCategory: TransferCategoryData = { category: 'Planned' };
    const unplannedCategory: TransferCategoryData = { category: 'Unplanned' };
    const undeterminedCategory: TransferCategoryData = { category: 'Undetermined' };

    // Aggregate counts by reason across all applications
    const reasonTotals: Record<string, { count: number; apps: { appId: string; count: number }[] }> = {};

    // Iterate through each application's transfer reasons
    Object.entries(apiResponse).forEach(([applicationId, reasons]) => {
        Object.entries(reasons).forEach(([reason, count]) => {
            if (count === 0) return;  // Skip zero counts

            // Skip Immediate Transfers - they are not containable
            if (reason === TransferReason.IMMEDIATE_TRANSFER) return;

            if (!reasonTotals[reason]) {
                reasonTotals[reason] = { count: 0, apps: [] };
            }
            reasonTotals[reason].count += count;
            reasonTotals[reason].apps.push({ appId: applicationId, count });
        });
    });

    // Map aggregated reasons to the correct category (planned vs unplanned vs undetermined)
    Object.entries(reasonTotals).forEach(([reason, data]) => {
        const reasonKey = reason as TransferReason;

        // Check if this reason exists in our enum and determine category
        // null = Undetermined, true = Planned, false = Unplanned
        const isPlanned = TRANSFER_REASON_IS_PLANNED[reasonKey];
        const targetCategory = isPlanned === null ? undeterminedCategory : isPlanned ? plannedCategory : unplannedCategory;

        targetCategory[reasonKey] = {
            value: data.count,
            apps: data.apps.map(app => ({
                appName: appNameMap?.[app.appId] ?? app.appId,  // Use app name from map if available, fallback to appId
                count: app.count,
                english: 0,  // Not available from this endpoint
                spanish: 0   // Not available from this endpoint
            }))
        } as ChartTransferReasonData;
    });

    return [plannedCategory, unplannedCategory, undeterminedCategory];
};

/**
 * Transform separate planned and unplanned TransferReasonsApiResponse to chart-compatible format
 * This function takes the API responses from two separate calls:
 * - plannedResponse: API called with getPlanned=true
 * - unplannedResponse: API called with getPlanned=false
 * 
 * API returns: { applicationId: { "REASON": count, ... }, ... }
 * Chart expects: [{ category: 'Planned', REASON: { value, apps } }, { category: 'Unplanned', ... }]
 * 
 * @param plannedResponse - The API response for planned transfers (getPlanned=true)
 * @param unplannedResponse - The API response for unplanned transfers (getPlanned=false)
 * @param appNameMap - Optional map of applicationId to application name for resolving app names
 */
export const transformSeparateTransferReasonsToChartData = (
    plannedResponse: TransferReasonsApiResponse | null,
    unplannedResponse: TransferReasonsApiResponse | null,
    appNameMap?: AppNameMap
): TransferCategoryData[] => {
    const plannedCategory: TransferCategoryData = { category: 'Planned' };
    const unplannedCategory: TransferCategoryData = { category: 'Unplanned' };

    // Helper function to aggregate reasons from an API response
    const aggregateReasons = (
        apiResponse: TransferReasonsApiResponse | null,
        targetCategory: TransferCategoryData
    ) => {
        if (!apiResponse || Object.keys(apiResponse).length === 0) return;

        const reasonTotals: Record<string, { count: number; apps: { appId: string; count: number }[] }> = {};

        Object.entries(apiResponse).forEach(([applicationId, reasons]) => {
            Object.entries(reasons).forEach(([reason, count]) => {
                if (count === 0) return;

                // Skip Immediate Transfers - they are not containable
                if (reason === TransferReason.IMMEDIATE_TRANSFER) return;

                if (!reasonTotals[reason]) {
                    reasonTotals[reason] = { count: 0, apps: [] };
                }
                reasonTotals[reason].count += count;
                reasonTotals[reason].apps.push({ appId: applicationId, count });
            });
        });

        Object.entries(reasonTotals).forEach(([reason, data]) => {
            const reasonKey = reason as TransferReason;
            targetCategory[reasonKey] = {
                value: data.count,
                apps: data.apps.map(app => ({
                    appName: appNameMap?.[app.appId] ?? app.appId,
                    count: app.count,
                    english: 0,
                    spanish: 0
                }))
            } as ChartTransferReasonData;
        });
    };

    aggregateReasons(plannedResponse, plannedCategory);
    aggregateReasons(unplannedResponse, unplannedCategory);

    return [plannedCategory, unplannedCategory];
};

/**
 * Calculate transfer totals from transfer chart data
 */
export const calculateTransferTotals = (
    transferData: TransferCategoryData[]
): { planned: number; unplanned: number; undetermined: number; total: number } => {
    let planned = 0;
    let unplanned = 0;
    let undetermined = 0;

    transferData.forEach(categoryData => {
        Object.keys(categoryData).forEach(key => {
            if (key !== 'category') {
                const reasonData = categoryData[key] as ChartTransferReasonData;
                if (reasonData?.value) {
                    if (categoryData.category === 'Planned') {
                        planned += reasonData.value;
                    } else if (categoryData.category === 'Unplanned') {
                        unplanned += reasonData.value;
                    } else if (categoryData.category === 'Undetermined') {
                        undetermined += reasonData.value;
                    }
                }
            }
        });
    });

    return { planned, unplanned, undetermined, total: planned + unplanned + undetermined };
};

/**
 * Transform OrganizationEngagement array to dashboard call records
 */
export const transformEngagementsToCallRecords = (
    engagements: OrganizationEngagement[] | null
): DashboardCallRecord[] => {
    if (!engagements) return [];

    return engagements.map(engagement => ({
        id: engagement.id,
        date: engagement.date || moment(engagement.eventDate || engagement.startDate).format('MM/DD/YYYY'),
        time: engagement.time || moment(engagement.eventDate || engagement.startDate).format('h:mm A'),
        phoneLastFour: engagement.phoneLastFour || engagement.nativeUserId?.slice(-4) || '****',
        transcriptLink: `/transcripts/${engagement.sessionId || engagement.id}`,
        category: engagement.sessionCategory || engagement.category || 'Unknown',
        tags: engagement.sessionTags || engagement.tags || [],
        location: engagement.location || engagement.applicationName || 'Unknown'
    }));
};

/**
 * Transform ContainedCallsData array to dashboard format for appointments managed chart
 */
export const transformContainedCallsData = (
    containedCallsData: ContainedCallsData[] | null,
    defaultColor: string
): DashboardContainedCallData[] => {
    if (!containedCallsData) return [];

    return containedCallsData.map(item => ({
        type: item.category,
        count: item.containedCount,
        color: defaultColor,
        apps: item.containedApps?.map((app: ApiAppBreakdown) => ({
            appName: app.applicationName,
            count: app.count,
            english: app.english,
            spanish: app.spanish
        })) || [],
        notContainedCount: item.notContainedCount,
        notContainedApps: item.notContainedApps?.map((app: ApiAppBreakdown) => ({
            appName: app.applicationName,
            count: app.count,
            english: app.english,
            spanish: app.spanish
        }))
    }));
};

/**
 * Determine if data is loading based on container loading states
 */
export const isAnalyticsDataLoading = (loadingStates: {
    loadingOrganizationEngagements?: boolean;
    loadingOrganizationTopLevelMetrics?: boolean;
    loadingOrganizationCallVolumeHistogram?: boolean;
    loadingOrganizationContainableCallVolumeHistogram?: boolean;
    loadingOrganizationContainedCallVolumeHistogram?: boolean;
    loadingOrganizationTransferReasons?: boolean;
}): boolean => {
    return (
        loadingStates.loadingOrganizationEngagements ||
        loadingStates.loadingOrganizationTopLevelMetrics ||
        loadingStates.loadingOrganizationCallVolumeHistogram ||
        loadingStates.loadingOrganizationContainableCallVolumeHistogram ||
        loadingStates.loadingOrganizationContainedCallVolumeHistogram ||
        loadingStates.loadingOrganizationTransferReasons ||
        false
    );
};

/**
 * Get default empty state for healthcare metrics
 */
export const getEmptyHealthcareMetrics = (): DashboardHealthcareMetrics => ({
    laborHoursSaved: 0,
    offHoursCalls: 0,
    offHoursScheduledAppts: 0,
    containedCallsCount: 0,
    totalEngagements: 0,
    newPatientBookings: 0,
    laborHoursSavedApps: [],
    offHoursApps: [],
    offHoursScheduledApptsApps: [],
    newPatientBookingsApps: [],
    containableCallsCount: 0
});

/**
 * Get default empty state for food ordering metrics
 */
export const getEmptyFoodOrderingMetrics = (): DashboardFoodOrderingMetrics => ({
    upsellSuccessRate: 0,
    successfulUpsells: 0,
    upsellAttempts: 0,
    averageTicketSize: 0,
    totalSold: 0,
    ordersPlaced: 0,
    questionsAnswered: 0,
    laborHoursSaved: 0,
    upsoldRevenueGenerated: 0,
    upsellApps: [],
    ticketApps: [],
    questionsApps: [],
    laborHoursSavedApps: []
});

/**
 * Get default empty histogram dataset
 */
export const getEmptyHistogramDataset = (id: string, color: string): HistogramDataset => ({
    id,
    color,
    data: []
});

// ==================== CONVERSION FUNNEL TYPES & TRANSFORMATIONS ====================

/**
 * Conversion funnel item format for the funnel chart
 */
export interface ConversionFunnelItem {
    id: string;
    label: string;
    value: number;
    color: string;
    apps: DashboardAppBreakdown[];
}

export interface EngagementConversionFunnelOptions {
    countCompletionOncePerEngagement?: boolean;
}

/**
 * Transform TopLevelMetrics API response to conversion funnel format for AI Ordering
 * The funnel shows: Total Calls -> Order Attempts (Containable) -> Orders Placed (Contained)
 */
export const transformToConversionFunnelData = (
    metrics: TopLevelMetrics | null,
    colors: { totalCalls: string; orderAttempts: string; ordersPlaced: string }
): ConversionFunnelItem[] => {
    if (!metrics) {
        return getEmptyConversionFunnelData(colors);
    }

    return [
        {
            id: 'total-calls',
            label: 'Total Calls',
            value: metrics.totalCalls ?? 0,
            color: colors.totalCalls,
            apps: transformAppBreakdowns(metrics.totalCallsApps)
        },
        {
            id: 'order-attempts',
            label: 'Containable Conversations (Order Attempts)',
            value: metrics.containableCalls ?? metrics.orderAttempts ?? 0,
            color: colors.orderAttempts,
            apps: transformAppBreakdowns(metrics.containableCallsApps ?? metrics.orderAttemptsApps)
        },
        {
            id: 'placed-orders',
            label: 'Contained Conversations (Placed Orders)',
            value: metrics.containedCalls ?? metrics.ordersPlaced ?? 0,
            color: colors.ordersPlaced,
            apps: transformAppBreakdowns(metrics.containedCallsApps ?? metrics.ordersPlacedApps)
        }
    ];
};

/**
 * Transform OrganizationEngagement[] to conversion funnel format for AI Ordering
 * Uses isContainable and isContained properties from engagement records
 * The funnel shows: Total Calls -> Containable Conversations (Order Attempts) -> Contained Conversations (Placed Orders)
 * 
 * @param engagements - Array of organization engagements from getOrganizationEngagements API
 * @param colors - Color configuration for funnel segments
 * @param appNameMap - Optional map of applicationId to application name for display
 * @param options - Optional counting behavior overrides for the calling dashboard
 * @returns ConversionFunnelItem[] for the funnel chart
 */
export const transformEngagementsToConversionFunnel = (
    engagements: OrganizationEngagement[] | null,
    colors: { totalCalls: string; orderAttempts: string; ordersPlaced: string },
    appNameMap?: AppNameMap,
    options: EngagementConversionFunnelOptions = {}
): ConversionFunnelItem[] => {
    if (!engagements || engagements.length === 0) {
        return getEmptyConversionFunnelData(colors);
    }

    // Count totals and group by application
    const totalCount = engagements.length;
    const actionAttemptedEngagements = engagements.filter(e => e.sessionCategory == "ACTION_ATTEMPTED");
    const orderPlacedEngagements = engagements.filter(e => e.sessionTags?.includes("ACTION_COMPLETED"));

    const ordersPlacedCount = options.countCompletionOncePerEngagement
        ? orderPlacedEngagements.length
        : orderPlacedEngagements.reduce(
            (sum, e) => sum + (e.sessionTags || []).filter(t => t === "ACTION_COMPLETED").length,
            0
        );

    // Aggregate by application for total calls
    const totalCallsApps = aggregateEngagementsByAppForFunnel(engagements, appNameMap);

    // Aggregate by application for containable calls
    const containableApps = aggregateEngagementsByAppForFunnel(actionAttemptedEngagements, appNameMap);

    // Aggregate by application for contained calls
    const containedApps = aggregateEngagementsByAppForFunnel(
        orderPlacedEngagements,
        appNameMap,
        options.countCompletionOncePerEngagement ? undefined : "ACTION_COMPLETED"
    );

    return [
        {
            id: 'total-calls',
            label: 'Total Calls',
            value: totalCount,
            color: colors.totalCalls,
            apps: totalCallsApps
        },
        {
            id: 'order-attempts',
            label: 'Order Attempts',
            value: actionAttemptedEngagements.length,
            color: colors.orderAttempts,
            apps: containableApps
        },
        {
            id: 'placed-orders',
            label: 'Placed Orders',
            value: ordersPlacedCount,
            color: colors.ordersPlaced,
            apps: containedApps
        }
    ];
};

/**
 * Helper function to aggregate engagements by application for funnel chart
 * Includes English/Spanish language breakdown by checking isSpanish flag,
 * language field, or SPANISH tag on each engagement.
 * When countTag is provided, counts tag occurrences per engagement instead of
 * counting engagements, to handle multi-action sessions.
 */
const aggregateEngagementsByAppForFunnel = (
    engagements: OrganizationEngagement[],
    appNameMap?: AppNameMap,
    countTag?: string
): DashboardAppBreakdown[] => {
    const appCounts = new Map<string, { appId: string; count: number; english: number; spanish: number }>();

    engagements.forEach(engagement => {
        const appId = engagement.applicationId || 'unknown';
        const existing = appCounts.get(appId) || { appId, count: 0, english: 0, spanish: 0 };

        const increment = countTag
            ? (engagement.sessionTags || []).filter(t => t === countTag).length
            : 1;

        if (increment === 0) return;
        existing.count += increment;

        // Detect Spanish by checking isSpanish flag, language field, or SPANISH tag
        const isSpanish = (engagement as any).isSpanish === true
            || ((engagement as any).language != null && String((engagement as any).language).trim().toLowerCase() === 'spanish')
            || ((engagement as any).language != null && String((engagement as any).language).trim().toLowerCase() === 'es')
            || (engagement.sessionTags || []).some((t: any) => t != null && String(t).trim().toLowerCase() === 'spanish');

        if (isSpanish) {
            existing.spanish += increment;
        } else {
            existing.english += increment;
        }
        appCounts.set(appId, existing);
    });

    return Array.from(appCounts.entries()).map(([appId, data]) => ({
        appName: (appNameMap && appNameMap[appId]) || appId,
        count: data.count,
        english: data.english,
        spanish: data.spanish
    }));
};

/**
 * Get empty conversion funnel data with proper structure
 */
export const getEmptyConversionFunnelData = (
    colors: { totalCalls: string; orderAttempts: string; ordersPlaced: string }
): ConversionFunnelItem[] => [
        { id: 'total-calls', label: 'Total Calls', value: 0, color: colors.totalCalls, apps: [] },
        { id: 'order-attempts', label: 'Order Attempts', value: 0, color: colors.orderAttempts, apps: [] },
        { id: 'placed-orders', label: 'Placed Orders', value: 0, color: colors.ordersPlaced, apps: [] }
    ];

/**
 * Calculate order completion rate from funnel data
 */
export const calculateOrderCompletionRate = (funnelData: ConversionFunnelItem[]): number => {
    const totalCalls = funnelData.find(d => d.id === 'total-calls')?.value ?? 0;
    const ordersPlaced = funnelData.find(d => d.id === 'placed-orders')?.value ?? 0;

    if (totalCalls === 0) return 0;
    return (ordersPlaced / totalCalls) * 100;
};

// ==================== PATIENT BOOKINGS TRANSFORMATIONS ====================

/**
 * Patient booking data format for the pie/bar chart
 */
export interface PatientBookingData {
    id: string;
    label: string;
    value: number;
    color: string;
    apps: DashboardAppBreakdown[];
}

/**
 * Aggregate engagements by application to get app-level counts
 */
const aggregateEngagementsByApp = (
    engagements: OrganizationEngagement[],
    appNameMap?: AppNameMap
): DashboardAppBreakdown[] => {
    const appCounts = new Map<string, { count: number; english: number; spanish: number }>();

    engagements.forEach(engagement => {
        // Resolve app name: try applicationName first, then lookup by applicationId, then location, then 'Unknown'
        const appName = engagement.applicationName ||
            (appNameMap && engagement.applicationId ? appNameMap[engagement.applicationId] : null) ||
            engagement.location ||
            'Unknown';
        const existing = appCounts.get(appName) || { count: 0, english: 0, spanish: 0 };

        existing.count++;

        // Check if engagement has Spanish tag (use sessionTags with fallback to tags)
        const tags = engagement.sessionTags || engagement.tags || [];
        const isSpanish = tags.includes('SPANISH');
        if (isSpanish) {
            existing.spanish++;
        } else {
            existing.english++;
        }

        appCounts.set(appName, existing);
    });

    return Array.from(appCounts.entries()).map(([appName, data]) => ({
        appName,
        count: data.count,
        english: data.english,
        spanish: data.spanish
    }));
};

/**
 * Check if an engagement has the NEW_PATIENT tag
 */
const isNewPatient = (engagement: OrganizationEngagement): boolean => {
    const tags = engagement.sessionTags || engagement.tags || [];
    return tags.includes('NEW_PATIENT');
};

/**
 * Transform scheduled engagements into Patient Bookings chart data
 * Separates into "New Patient" vs "Existing Patient" based on NEW_PATIENT tag
 * 
 * @param scheduledEngagements - Array of engagement records
 * @param colors - Color configuration for chart segments
 * @param appNameMap - Optional map of applicationId to application name for resolving app names
 */
export const transformEngagementsToPatientBookings = (
    scheduledEngagements: OrganizationEngagement[],
    colors: { newPatient: string; existingPatient: string },
    appNameMap?: AppNameMap
): PatientBookingData[] => {
    if (!scheduledEngagements || scheduledEngagements.length === 0) {
        return [
            { id: 'New Patient', label: 'New Patient', value: 0, color: colors.newPatient, apps: [] },
            { id: 'Existing Patient', label: 'Existing Patient', value: 0, color: colors.existingPatient, apps: [] }
        ];
    }

    // Separate engagements by new vs existing patient
    const newPatientEngagements = scheduledEngagements.filter(isNewPatient);
    const existingPatientEngagements = scheduledEngagements.filter(e => !isNewPatient(e));

    return [
        {
            id: 'New Patient',
            label: 'New Patient',
            value: newPatientEngagements.length,
            color: colors.newPatient,
            apps: aggregateEngagementsByApp(newPatientEngagements, appNameMap)
        },
        {
            id: 'Existing Patient',
            label: 'Existing Patient',
            value: existingPatientEngagements.length,
            color: colors.existingPatient,
            apps: aggregateEngagementsByApp(existingPatientEngagements, appNameMap)
        }
    ];
};

/**
 * Transform all scheduling engagements into Appointments Managed chart data
 * Used for the bar chart showing Schedule, Reschedule, Cancel, Check Next Appt
 * 
 * @param scheduledEngagements - Engagements for scheduled appointments
 * @param rescheduledEngagements - Engagements for rescheduled appointments
 * @param cancelledEngagements - Engagements for cancelled appointments
 * @param checkNextApptEngagements - Engagements for checking next appointments
 * @param color - Color for chart bars
 * @param appNameMap - Optional map of applicationId to application name for resolving app names
 */
export interface SchedulingEventData {
    type: string;
    count: number;
    color: string;
    apps: DashboardAppBreakdown[];
    notContainedCount?: number;
    notContainedApps?: DashboardAppBreakdown[];
}

export const transformEngagementsToSchedulingEvents = (
    scheduledEngagements: OrganizationEngagement[],
    rescheduledEngagements: OrganizationEngagement[],
    cancelledEngagements: OrganizationEngagement[],
    checkNextApptEngagements: OrganizationEngagement[],
    color: string,
    appNameMap?: AppNameMap
): SchedulingEventData[] => {
    return [
        {
            type: 'Schedule',
            count: scheduledEngagements?.length ?? 0,
            color,
            apps: aggregateEngagementsByApp(scheduledEngagements || [], appNameMap)
        },
        {
            type: 'Reschedule',
            count: rescheduledEngagements?.length ?? 0,
            color,
            apps: aggregateEngagementsByApp(rescheduledEngagements || [], appNameMap)
        },
        {
            type: 'Cancel',
            count: cancelledEngagements?.length ?? 0,
            color,
            apps: aggregateEngagementsByApp(cancelledEngagements || [], appNameMap)
        },
        {
            type: 'Check Appt',
            count: checkNextApptEngagements?.length ?? 0,
            color,
            apps: aggregateEngagementsByApp(checkNextApptEngagements || [], appNameMap)
        }
    ];
};

/**
 * Calculate total bookings from patient booking data
 */
export const calculateTotalBookings = (data: PatientBookingData[] | null): number => {
    if (!data) return 0;
    return data.reduce((sum, item) => sum + item.value, 0);
};

/**
 * Transform TopLevelMetrics per-app data into Appointments Managed chart data
 * Uses the scheduling metrics from the API (attempts vs completed, English vs Spanish)
 * 
 * API fields used:
 * - totalScheduleAttemptsEnglish/Spanish - attempts to schedule (containable)
 * - totalSchedulesCompletedEnglish/Spanish - completed schedules (contained)
 * - totalRescheduleAttemptsEnglish/Spanish - attempts to reschedule (containable)
 * - totalReschedulesCompletedEnglish/Spanish - completed reschedules (contained)
 * - totalCancellationAttemptsEnglish/Spanish - attempts to cancel (containable)
 * - totalCancellationsCompletedEnglish/Spanish - completed cancellations (contained)
 * - totalCheckNextAppointmentAttemptsEnglish/Spanish - attempts to check appointment (containable)
 * - totalCheckNextAppointmentCompletedEnglish/Spanish - completed checks (contained)
 * 
 * @param perAppMetrics - Record of applicationId to TopLevelMetrics
 * @param color - Color for chart bars
 * @param appNameMap - Optional map of applicationId to application name
 * @param applicationIds - Optional filter for specific application IDs
 * @param spanishOnly - If true, only aggregate Spanish metrics
 */
export const transformTopLevelMetricsToSchedulingEvents = (
    perAppMetrics: Record<string, TopLevelMetrics> | null | undefined,
    color: string,
    appNameMap?: AppNameMap,
    applicationIds?: Set<string> | string[],
    spanishOnly?: boolean
): SchedulingEventData[] => {
    if (!perAppMetrics || typeof perAppMetrics !== 'object') {
        return [];
    }

    // Convert to Set for efficient lookup
    // When applicationIds is undefined/null, include all apps (initial state before apps load)
    // When applicationIds is an empty Set/Array, return empty (user deselected all)
    let appIdSet: Set<string> | null = null;
    if (applicationIds !== undefined && applicationIds !== null) {
        if (applicationIds instanceof Set) {
            if (applicationIds.size === 0) return []; // Empty filter = no data
            appIdSet = applicationIds;
        } else {
            if (applicationIds.length === 0) return []; // Empty filter = no data
            appIdSet = new Set(applicationIds);
        }
    }

    // Helper to aggregate metrics for a specific event type
    const aggregateEventType = (
        getAttempts: (m: any) => { english: number; spanish: number },
        getCompleted: (m: any) => { english: number; spanish: number }
    ) => {
        let totalAttempts = 0;
        let totalCompleted = 0;
        const completedApps: DashboardAppBreakdown[] = [];
        const notCompletedApps: DashboardAppBreakdown[] = [];

        for (const [appId, metrics] of Object.entries(perAppMetrics)) {
            // Skip if filtering and this app is not selected
            if (appIdSet && !appIdSet.has(appId)) {
                continue;
            }

            if (metrics) {
                const attempts = getAttempts(metrics);
                const completed = getCompleted(metrics);

                // When spanishOnly is true, only use Spanish metrics
                const attemptsTotal = spanishOnly ? attempts.spanish : (attempts.english + attempts.spanish);
                const completedTotal = spanishOnly ? completed.spanish : (completed.english + completed.spanish);
                const notCompletedTotal = attemptsTotal - completedTotal;

                totalAttempts += attemptsTotal;
                totalCompleted += completedTotal;

                // App name lookup - appNameMap is Record<string, string>
                const appName = appNameMap?.[appId] || appId;

                // Add to completed apps if there are completed events
                if (completedTotal > 0) {
                    completedApps.push({
                        appName,
                        count: completedTotal,
                        english: spanishOnly ? 0 : completed.english,
                        spanish: completed.spanish
                    });
                }

                // Add to not-completed apps (attempts - completed) if there are any
                if (notCompletedTotal > 0) {
                    notCompletedApps.push({
                        appName,
                        count: notCompletedTotal,
                        english: spanishOnly ? 0 : Math.max(0, attempts.english - completed.english),
                        spanish: Math.max(0, attempts.spanish - completed.spanish)
                    });
                }
            }
        }

        return {
            totalAttempts,
            totalCompleted,
            notCompleted: totalAttempts - totalCompleted,
            completedApps,
            notCompletedApps
        };
    };

    // Schedule events
    const schedule = aggregateEventType(
        (m) => ({
            english: m.totalScheduleAttemptsEnglish ?? 0,
            spanish: m.totalScheduleAttemptsSpanish ?? 0
        }),
        (m) => ({
            english: m.totalSchedulesCompletedEnglish ?? 0,
            spanish: m.totalSchedulesCompletedSpanish ?? 0
        })
    );

    // Reschedule events
    const reschedule = aggregateEventType(
        (m) => ({
            english: m.totalRescheduleAttemptsEnglish ?? 0,
            spanish: m.totalRescheduleAttemptsSpanish ?? 0
        }),
        (m) => ({
            english: m.totalReschedulesCompletedEnglish ?? 0,
            spanish: m.totalReschedulesCompletedSpanish ?? 0
        })
    );

    // Cancel events
    const cancel = aggregateEventType(
        (m) => ({
            english: m.totalCancellationAttemptsEnglish ?? 0,
            spanish: m.totalCancellationAttemptsSpanish ?? 0
        }),
        (m) => ({
            english: m.totalCancellationsCompletedEnglish ?? 0,
            spanish: m.totalCancellationsCompletedSpanish ?? 0
        })
    );

    // Check appointment events
    const checkAppt = aggregateEventType(
        (m) => ({
            english: m.totalCheckNextAppointmentAttemptsEnglish ?? 0,
            spanish: m.totalCheckNextAppointmentAttemptsSpanish ?? 0
        }),
        (m) => ({
            english: m.totalCheckNextAppointmentCompletedEnglish ?? 0,
            spanish: m.totalCheckNextAppointmentCompletedSpanish ?? 0
        })
    );

    return [
        {
            type: 'Schedule',
            count: schedule.totalCompleted,
            color,
            apps: schedule.completedApps,
            notContainedCount: schedule.notCompleted,
            notContainedApps: schedule.notCompletedApps
        },
        {
            type: 'Reschedule',
            count: reschedule.totalCompleted,
            color,
            apps: reschedule.completedApps,
            notContainedCount: reschedule.notCompleted,
            notContainedApps: reschedule.notCompletedApps
        },
        {
            type: 'Cancel',
            count: cancel.totalCompleted,
            color,
            apps: cancel.completedApps,
            notContainedCount: cancel.notCompleted,
            notContainedApps: cancel.notCompletedApps
        },
        {
            type: 'Check Appt',
            count: checkAppt.totalCompleted,
            color,
            apps: checkAppt.completedApps,
            notContainedCount: checkAppt.notCompleted,
            notContainedApps: checkAppt.notCompletedApps
        }
    ];
};

// ==================== ENGAGEMENT TO SESSION REVIEW MAPPING ====================

/**
 * Engagement stats computed from pre-built engagement records.
 * Replaces the stats previously computed by UseProcessConversationalSuccessEvents.
 */
export interface EngagementStats {
    uniqueEngagementCount: number;
    convertedCustomers: number;
    repeatCustomers: number;
    upsellPercentage: number;
}

/**
 * Parse the sessionSummaries JSON string from an engagement into a formatted summary.
 * The backend stores summaries as JSON: {"sessionId1": "summary1", "sessionId2": "summary2"}
 * This reconstructs the "Call #1 (time)\nsummary" format used by the old hook.
 */
const parseEngagementSummaries = (engagement: OrganizationEngagement): string => {
    if (!engagement.sessionSummaries) {
        return 'No summary';
    }

    try {
        const summariesMap: Record<string, string> = JSON.parse(engagement.sessionSummaries);
        const entries = Object.entries(summariesMap);

        if (entries.length === 0) {
            return 'No summary';
        }

        if (entries.length === 1) {
            const summary = entries[0][1];
            return summary || 'No summary';
        }

        // Multiple sessions merged into one engagement - format with call numbers
        return entries
            .map(([, summary], index) => {
                const callLabel = `Call #${index + 1}`;
                const summaryText = summary || 'No summary available';
                return `${callLabel}\n${summaryText}`;
            })
            .join('\n\n')
            .replace(/\n\s*\n/g, '\n');
    } catch {
        // If parsing fails, use the raw string as the summary
        return engagement.sessionSummaries || 'No summary';
    }
};

/**
 * Maps an array of OrganizationEngagement records (from the backend engagement endpoints)
 * into SessionReview objects compatible with the existing table/filter/export logic.
 *
 * This replaces the deduplication + conversion + 10-minute merging logic that was
 * previously performed by UseProcessConversationalSuccessEvents, since the backend
 * now builds engagement records in real time with all of that already done.
 *
 * @param engagements - Pre-built engagement records from the backend
 * @param crossOrgMode - If true, preserves internal tags for every user
 */
export const mapEngagementsToSessionReviews = (
    engagements: OrganizationEngagement[],
    crossOrgMode: boolean = false
): SessionReview[] => {
    if (!engagements || engagements.length === 0) {
        return [];
    }

    return engagements.map(engagement => {
        const summary = parseEngagementSummaries(engagement);

        // Derive mergedSessions from sessionSummaries keys
        let mergedSessions: Array<{ sessionId: string; startDate: string | null }> = [];
        if (engagement.sessionSummaries) {
            try {
                const summariesMap: Record<string, string> = JSON.parse(engagement.sessionSummaries);
                mergedSessions = Object.keys(summariesMap).map(sessionId => ({
                    sessionId,
                    startDate: null // The backend doesn't store per-session start dates in summaries
                }));
            } catch {
                mergedSessions = [{
                    sessionId: engagement.sessionId,
                    startDate: engagement.eventDate || null
                }];
            }
        } else {
            mergedSessions = [{
                sessionId: engagement.sessionId,
                startDate: engagement.eventDate || null
            }];
        }

        // Internal users retain diagnostic tags outside cross-organization mode.
        let tags = [...(engagement.sessionTags || [])];
        if (!crossOrgMode) {
            tags = filterInternalVoicifyTags(tags);
        }

        return {
            sessionId: engagement.sessionId,
            summary,
            tags,
            category: engagement.sessionCategory,
            applicationId: engagement.applicationId,
            date: new Date(engagement.eventDate || 0),
            userId: engagement.nativeUserId,
            mergedSessions
        } as SessionReview;
    });
};

/**
 * Computes engagement-level statistics from pre-built engagement records.
 * This replaces the stats computation in UseProcessConversationalSuccessEvents.
 *
 * - uniqueEngagementCount: total number of engagement records (already deduplicated/merged by backend)
 * - upsellPercentage: percentage of engagements with UPSELL_SUCCESSFUL out of those with UPSELL_ATTEMPTED
 * - convertedCustomers: users who had a TRANSFERRED engagement before an ACTION_COMPLETED engagement
 * - repeatCustomers: users with more than one ACTION_COMPLETED engagement
 *
 * @param engagements - Pre-built engagement records from the backend
 */
export const computeEngagementStats = (
    engagements: OrganizationEngagement[]
): EngagementStats => {
    if (!engagements || engagements.length === 0) {
        return {
            uniqueEngagementCount: 0,
            convertedCustomers: 0,
            repeatCustomers: 0,
            upsellPercentage: 0
        };
    }

    // Unique engagement count is simply the number of engagement records
    const uniqueEngagementCount = engagements.length;

    // Upsell percentage
    let upsellAttempts = 0;
    let upsellCompletes = 0;
    for (const engagement of engagements) {
        const tags = engagement.sessionTags || [];
        if (tags.includes('UPSELL_ATTEMPTED')) {
            upsellAttempts++;
        }
        if (tags.includes('UPSELL_SUCCESSFUL')) {
            upsellCompletes++;
        }
    }
    const upsellPercentage = upsellAttempts > 0 ? (upsellCompletes / upsellAttempts) * 100 : 0;

    // Converted and repeat customers - group by userId, walk chronologically
    const groupedByUserId: Record<string, OrganizationEngagement[]> = {};
    for (const engagement of engagements) {
        const userId = engagement.nativeUserId || 'anonymous';
        if (!groupedByUserId[userId]) {
            groupedByUserId[userId] = [];
        }
        groupedByUserId[userId].push(engagement);
    }

    let convertedCustomers = 0;
    let repeatCustomers = 0;

    Object.values(groupedByUserId).forEach(userEngagements => {
        // Sort by event date ascending
        userEngagements.sort((a, b) =>
            new Date(a.eventDate || 0).getTime() - new Date(b.eventDate || 0).getTime()
        );

        let hasTransfer = false;
        let hasConverted = false;
        let actionCompletedCount = 0;

        for (const engagement of userEngagements) {
            const tags = engagement.sessionTags || [];
            if (tags.includes('TRANSFERRED')) {
                hasTransfer = true;
                continue;
            }
            if (tags.includes('ACTION_COMPLETED')) {
                actionCompletedCount++;
                if (hasTransfer && !hasConverted) {
                    convertedCustomers++;
                    hasConverted = true;
                }
            }
        }

        if (actionCompletedCount > 1) {
            repeatCustomers++;
        }
    });

    return {
        uniqueEngagementCount,
        convertedCustomers,
        repeatCustomers,
        upsellPercentage
    };
};

/**
 * Converts a date filter with naive date strings (e.g., DATE_FORMAT 'YYYY-MM-DD hh:mm a')
 * into ISO 8601 strings that include the local UTC offset.
 *
 * @param startDate - Start date string (any moment-parseable format)
 * @param endDate - End date string (any moment-parseable format)
 * @returns Filter object with ISO dates including UTC offset and timezone set to undefined
 */
export const buildTimezoneAwareDateFilter = (
    startDate: string,
    endDate: string
): { startDate: string; endDate: string; timezone: undefined } => {
    return normalizeEngagementAnalyticsFilter({ startDate, endDate, timezone: undefined });
};

/**
 * Loads all engagement records for an application with pagination.
 * Pages through the endpoint in batches until all records are fetched.
 *
 * @param loadFn - The API function to call (e.g., getApplicationEngagements or getOrganizationEngagements)
 * @param id - The applicationId or organizationId
 * @param filter - The base filter (startDate, endDate, timezone)
 * @param pageSize - Number of records per page (default 5000)
 * @param maxRecords - Maximum total records to fetch (default 200000)
 * @param logCallback - Optional callback for progress logging
 */
export const loadEngagementsPaged = async (
    loadFn: (id: string, filter: any) => Promise<any>,
    id: string,
    filter: { startDate: string; endDate: string; timezone?: string },
    pageSize: number = 5000,
    maxRecords: number = 200000,
    logCallback?: (message: string) => void
): Promise<OrganizationEngagement[]> => {
    const normalizedFilter = normalizeEngagementAnalyticsFilter(filter);
    const allEngagements: OrganizationEngagement[] = [];
    let skip = 0;
    let hasMore = true;

    while (hasMore && allEngagements.length < maxRecords) {
        const pagedFilter = {
            ...normalizedFilter,
            skip,
            take: pageSize
        };

        if (logCallback) {
            logCallback(`Loading engagements... (${allEngagements.length} loaded so far)`);
        }

        const result = await loadFn(id, pagedFilter);
        if (!result || result.resultType !== 'Ok') {
            throw new Error(result?.errors?.[0] ?? 'Failed to load engagements page');
        }
        const batch = result.data || [];

        allEngagements.push(...batch);
        skip += pageSize;
        hasMore = batch.length === pageSize;
    }

    if (logCallback) {
        logCallback(`Loaded ${allEngagements.length} engagements total`);
    }

    return allEngagements;
};
